import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  List,
  Modal,
  Notification,
  SearchForm,
  Checkbox,
} from 'shared/components';
import { displayValue, formatDate, formatOrderBy } from 'shared/helpers';
import { searchType } from 'shared/constants';
import api from 'shared/api';
import { addAccountOrders } from '../../../actions';

const PAGE_SIZE = 20;

const getOrderIdentifier = (order) => get(order, 'id');

const AddOrdersModal = ({ accountId, closeCb, confirmCb }) => {
  const { t } = useTranslation();
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [activeQuery, setActiveQuery] = useState('');
  const [searchBy, setSearchBy] = useState(searchType.order_storeID);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isSubmitting, setSubmitting] = useState(false);

  const fetchAvailableOrders = useCallback(() => {
    if (!accountId) {
      setAvailableOrders([]);
      setPages(1);
      return;
    }
    setLoading(true);
    setError(false);
    const queryUrl = activeQuery ? `&${searchBy}=${activeQuery}` : '';
    const sortUrl = formatOrderBy(currentSort);
    const url = `/api/v1/orders/?limit=${PAGE_SIZE}&offset=${
      page * PAGE_SIZE
    }${queryUrl}${sortUrl}&customer__account__not=${accountId}`;

    api
      .get(url)
      .then((res) => {
        const results = get(res, 'data.results', []);
        const count = get(res, 'data.count', results.length);
        setAvailableOrders(results);
        setPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setAvailableOrders([]);
        setPages(1);
        setLoading(false);
      });
  }, [accountId, activeQuery, currentSort, page, searchBy]);

  useEffect(() => {
    fetchAvailableOrders();
  }, [fetchAvailableOrders]);

  const toggleOrderSelection = (order) => {
    const orderId = getOrderIdentifier(order);
    if (orderId === undefined || orderId === null) {
      return;
    }
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : prev.concat(orderId),
    );
  };

  const handleSubmit = () => {
    if (isSubmitting || !selectedOrders.length) {
      return false;
    }

    setSubmitting(true);
    return addAccountOrders(accountId, selectedOrders)
      .then(() => {
        setSubmitting(false);
        Notification('success', t('Changes saved successfully'));
        confirmCb();
        closeCb();
        return true;
      })
      .catch(() => {
        setSubmitting(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
        return false;
      });
  };

  const handleSearchSubmit = (value) => {
    setActiveQuery(value);
    setPage(0);
  };

  const handleClear = () => {
    setActiveQuery('');
    setPage(0);
  };

  const handleSearchTypeChange = (newType) => {
    setSearchBy(newType);
    setPage(0);
  };

  const handleSortChange = (newSorted) => {
    setCurrentSort(newSorted);
    setPage(0);
  };

  const handleRetry = () => fetchAvailableOrders();

  const columns = [
    {
      id: 'select',
      className: 'select-container',
      Cell: (rowData) => {
        const orderId = getOrderIdentifier(rowData.original);
        const isSelected = selectedOrders.includes(orderId);
        return (
          <button
            className="table-select"
            aria-label={t('Select order')}
            onClick={(event) => {
              event.stopPropagation();
              toggleOrderSelection(rowData.original);
            }}
            type="button"
          >
            <Checkbox
              inputId={`order-${orderId || rowData.index}-select`}
              handleChange={() => {}}
              checked={isSelected}
            />
          </button>
        );
      },
      width: 60,
      show: true,
      sortable: false,
    },
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 180,
    },
    {
      Header: t('Order ID'),
      accessor: 'store_id',
      Cell: (cellData) => displayValue(cellData.value),
    },
  ];

  return (
    <Modal
      closeCb={closeCb}
      confirmCb={handleSubmit}
      disabled={isSubmitting || !selectedOrders.length}
      title={t('Add orders')}
      size="lg"
    >
      <div className="CustomerAccountOrdersModal">
        <div className="list-header">
          <SearchForm
            options={[
              {
                label: t('Search by order id'),
                value: searchType.order_storeID,
              },
            ]}
            selectedValue={searchBy}
            handleSearchTypeChange={handleSearchTypeChange}
            handleSearchSubmit={handleSearchSubmit}
            activeQuery={activeQuery}
            handleClear={handleClear}
            disabled={loading}
          />
          <div />
        </div>
        {loading ? (
          <ContentLoader text={t('Loading orders')} />
        ) : error ? (
          <div className="CustomerAccount-error">
            <p>{t('Unable to load orders')}</p>
            <Button theme="info" size="sm" onClick={handleRetry}>
              {t('Retry')}
            </Button>
          </div>
        ) : availableOrders.length ? (
          <List
            data={availableOrders}
            columns={columns}
            loading={loading}
            manual
            page={page}
            pages={pages}
            minRows={availableOrders.length || 5}
            showPagination={pages > 1}
            onPageChange={setPage}
            onSortedChange={handleSortChange}
            defaultSorted={currentSort}
          />
        ) : (
          <div className="CustomerAccount-empty">
            {t('No available orders')}
          </div>
        )}
      </div>
    </Modal>
  );
};

AddOrdersModal.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
};

export default AddOrdersModal;
